/**
 * Shared SafeArea wiring for the web/viewjob entry points (index.library.tsx,
 * index.client.ts, index.server.ts).
 *
 * IMPORTANT: This module must NOT import from `@indeed/jobsearch-react-native-bundle/*`.
 * The strict bundle is produced by `scripts/prepare-viewjob-strict.sh`, whose sed
 * codemod rewrites `jobsearch-react-native-bundle/<dir>/` import specifiers to their
 * `-strict` variants ONLY in the three index.* entry files. This shared module is
 * imported via a relative path and is never sed-processed, so it must stay
 * bundle-agnostic. Each entry file keeps its own (strict-rewritable) App import and
 * passes the component into {@link createAppWithSafeArea}.
 */
import type { ComponentType } from "react";

import { createElement } from "react";
import { SafeAreaProvider } from "react-native-safe-area-context";

/**
 * Initial SafeAreaProvider metrics. Must be a truthy object so
 * SafeAreaProviderCompat (inside react-native-screens' NativeStackView) takes the
 * styled `flex: 1` path on first render. With a null inset context the height chain
 * collapses and the screen content never lays out — nothing can scroll until an
 * async re-render supplies real insets.
 */
export const INITIAL_SAFE_AREA_METRICS = {
  frame: { x: 0, y: 0, width: 0, height: 0 },
  insets: { top: 0, left: 0, right: 0, bottom: 0 },
};

/**
 * Wrap `Inner` in a SafeAreaProvider seeded with {@link INITIAL_SAFE_AREA_METRICS}
 * so react-native-screens' NativeStackView lays out on web.
 *
 * `Inner` is supplied by the caller (rather than imported here) so the strict
 * codemod can rewrite each entry file's App import independently — see the module
 * header.
 */
export function createAppWithSafeArea<P extends object>(Inner: ComponentType<P>): ComponentType<P> {
  return function AppWithSafeArea(props: P) {
    return createElement(
      SafeAreaProvider,
      { initialMetrics: INITIAL_SAFE_AREA_METRICS },
      createElement(Inner, props),
    );
  };
}
